// Small shared pieces for the Sales tab: stage pills, rep chips, a sheet
// (right panel on desktop, bottom sheet on phones), form bits.
import React, { useEffect } from 'react';
import { Flame, Snowflake, Sun, X } from 'lucide-react';
import { TEMPS, isCallback, isGoingCold, stageOf, storeClock, tempOf, tierOf } from './salesPlan';

export const TONE = {
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  zinc: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  red: 'bg-red-50 text-red-700 border-red-200',
};

export const DOT = {
  slate: 'bg-slate-400',
  blue: 'bg-blue-600',
  violet: 'bg-violet-600',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-600',
  zinc: 'bg-zinc-400',
  red: 'bg-red-500',
};

export const REP_COLORS = {
  blue: 'bg-blue-600',
  emerald: 'bg-emerald-600',
  violet: 'bg-violet-600',
  amber: 'bg-amber-500',
  rose: 'bg-rose-600',
  cyan: 'bg-cyan-600',
};
export const REP_COLOR_KEYS = Object.keys(REP_COLORS);

export const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';

export const inputCls =
  'w-full px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20';
export const btnPrimary =
  'inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed disabled:shadow-none';
export const btnGhost =
  'inline-flex items-center justify-center gap-1.5 h-10 px-3.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed';
export const btnSmall =
  'inline-flex items-center justify-center gap-1 h-8 px-2.5 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50';

export function StagePill({ stage, className = '', prospect = null }) {
  const s = stageOf(stage, prospect);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-bold whitespace-nowrap ${TONE[s.tone]} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${DOT[s.tone]}`} />
      {s.label}
    </span>
  );
}

export function RepChip({ rep, withName = false, size = 'sm', className = '' }) {
  const dim = size === 'lg' ? 'w-9 h-9 text-sm' : size === 'md' ? 'w-7 h-7 text-[11px]' : 'w-6 h-6 text-[10px]';
  if (!rep) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`} title="Nobody owns this store yet">
        <span className={`${dim} rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center font-bold`}>?</span>
        {withName && <span className="text-xs text-slate-400">Unassigned</span>}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} title={rep.name}>
      <span className={`${dim} rounded-full ${REP_COLORS[rep.color] || REP_COLORS.blue} text-white flex items-center justify-center font-bold flex-shrink-0`}>
        {initials(rep.name)}
      </span>
      {withName && <span className="text-xs font-semibold text-slate-700 truncate">{rep.name}</span>}
    </span>
  );
}

/**
 * Sheet: a panel that slides in from the right on sm+ and up from the
 * bottom on phones. Escape and the backdrop close it.
 */
export function Sheet({ open, onClose, title, subtitle, children, footer, width = 'md', zIndex = 100 }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  const w = width === 'lg' ? 'sm:w-[640px] lg:w-[720px]' : width === 'sm' ? 'sm:w-[440px]' : 'sm:w-[540px]';
  return (
    <div className="fixed inset-0 flex items-end sm:items-stretch sm:justify-end" style={{ zIndex }} role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className={`relative w-full ${w} max-h-[92vh] sm:max-h-none sm:h-full bg-white shadow-2xl rounded-t-2xl sm:rounded-none flex flex-col max-w-none`} style={{ maxWidth: 'none' }}>
        <div className="flex-shrink-0 px-5 pt-4 pb-3 border-b border-slate-200 flex items-start gap-3">
          <div className="sm:hidden absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-slate-300" />
          <div className="flex-1 min-w-0 pt-1 sm:pt-0">
            {typeof title === 'string' ? <h2 className="text-lg font-bold text-slate-900 truncate">{title}</h2> : title}
            {subtitle && <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex-shrink-0 px-5 py-3 border-t border-slate-200 bg-slate-50">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

export function Empty({ icon: Icon, title, body, action }) {
  return (
    <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-10 text-center">
      {Icon && (
        <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mx-auto mb-3">
          <Icon className="w-6 h-6 text-slate-400" />
        </div>
      )}
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      {body && <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">{body}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

// ---------- hot / warm / cold, tier, call-back ----------
const TEMP_STYLE = {
  hot: { on: 'bg-red-600 text-white border-red-600', off: 'text-red-700 border-red-200 hover:bg-red-50', icon: Flame },
  warm: { on: 'bg-amber-500 text-white border-amber-500', off: 'text-amber-800 border-amber-200 hover:bg-amber-50', icon: Sun },
  cold: { on: 'bg-sky-600 text-white border-sky-600', off: 'text-sky-700 border-sky-200 hover:bg-sky-50', icon: Snowflake },
};

/** Read-only chip: the store's temperature, with "going cold" when a hot store sat too long. */
export function TempChip({ prospect: p, small = false }) {
  const t = tempOf(p);
  if (!t) return null;
  const st = TEMP_STYLE[t];
  const Icon = st.icon;
  const cold = isGoingCold(p);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-bold ${small ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-[11px]'} ${st.on}`} title={TEMPS.find((x) => x.key === t)?.hint}>
      <Icon className={small ? 'w-2.5 h-2.5' : 'w-3 h-3'} /> {cold ? 'Hot, going cold' : TEMPS.find((x) => x.key === t)?.label}
    </span>
  );
}

/** One tap to set hot / warm / cold. Tap the lit one again to clear it. */
export function TempPicker({ prospect: p, onPick, disabled = false }) {
  const cur = tempOf(p);
  return (
    <div className="inline-flex items-center gap-1" role="group" aria-label="How warm is this store">
      {TEMPS.map((t) => {
        const st = TEMP_STYLE[t.key];
        const Icon = st.icon;
        const on = cur === t.key;
        return (
          <button key={t.key} type="button" disabled={disabled} onClick={() => onPick(on ? '' : t.key)} title={t.hint} className={`inline-flex items-center gap-1 h-7 px-2 rounded-full border text-[11px] font-bold transition-colors disabled:opacity-50 ${on ? st.on : `bg-white ${st.off}`}`}>
            <Icon className="w-3 h-3" /> {t.label}
          </button>
        );
      })}
    </div>
  );
}

export function TierChip({ prospect: p, small = false }) {
  const t = tierOf(p);
  if (!t) return null;
  const tone = t === 'A' ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : t === 'B' ? 'border-slate-300 bg-white text-slate-700' : 'border-slate-200 bg-slate-50 text-slate-500';
  return <span className={`inline-flex items-center rounded-full border font-bold ${small ? 'px-1.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'} ${tone}`} title="Fit from the research: A is the best fit">Tier {t}</span>;
}

export function CallbackChip({ prospect: p }) {
  if (!isCallback(p)) return null;
  return <span className="inline-flex items-center rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-[11px] font-bold text-violet-800">{p.next_action}</span>;
}

/** Their local time when it differs from ours, red when the showroom is likely closed. */
export function StoreClock({ prospect: p }) {
  const c = storeClock(p);
  if (!c) return null;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${c.open ? 'border-slate-200 bg-white text-slate-700' : 'border-red-200 bg-red-50 text-red-700'}`} title={c.open ? 'Their local time' : 'Their local time. The showroom is probably closed'}>{c.text} their time{c.open ? '' : ', likely closed'}</span>;
}
